import React, { useState, useRef, useEffect } from 'react';
import { FileText, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx'];
const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

const ReplaceResumeButton = ({ onResumeReplaced }) => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [currentResume, setCurrentResume] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    if (user) fetchCurrentResume();
  }, [user]);

  const getAuthToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  };

  const fetchCurrentResume = async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;
      const res = await fetch(`${API_BASE}/api/user/resume`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentResume(data.resume);
      }
    } catch (e) {
      console.warn('Could not fetch resume info:', e);
    } finally {
      setIsFetching(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error('Invalid file type. Please upload a PDF or DOCX file.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setIsLoading(true);
    const loadingToastId = toast.loading('Uploading resume...');

    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');

      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_BASE}/api/user/resume`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }

      const data = await res.json();

      setCurrentResume({
        filename: file.name,
        uploaded_at: data.uploaded_at || new Date().toISOString(),
      });

      toast.dismiss(loadingToastId);
      toast.success('Resume updated!');

      if (onResumeReplaced) onResumeReplaced(data);
    } catch (err) {
      toast.dismiss(loadingToastId);
      toast.error(`Failed to update resume: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-3 p-1">
      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
        <FileText className="w-5 h-5 text-slate-500 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          {isFetching ? (
            <p className="text-sm text-slate-400">Loading...</p>
          ) : currentResume ? (
            <>
              <p className="text-sm font-medium text-slate-800 truncate">{currentResume.filename}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Last updated: {formatDate(currentResume.uploaded_at)}
              </p>
            </>
          ) : (
            <p className="text-sm text-slate-500">No resume on file</p>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        className="hidden"
        onChange={handleFileChange}
      />

      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
        ) : (
          <><RefreshCw className="w-4 h-4" /> Replace Resume</>
        )}
      </button>
      <p className="text-xs text-slate-400 text-center">PDF or DOCX · Max 5MB</p>
    </div>
  );
};

export default ReplaceResumeButton;
